import Block from "@/components/Block";
import EventIndex from "@/components/EventIndex";
import EventsCarousel from "@/components/EventsCarousel";
import Heading from "@/components/Heading";
import HorizontalRule from "@/components/HorizontalRule";
import Page from "@/components/Page";
import Seomatic from "@/components/Seomatic";
import useTrack from "@/hooks/useTrack";
import Layout from "@/layouts/Layout";
import craftBatch from "@/lib/craftBatch";
import getAfterNowQueryArgument from "@/lib/getAfterNowQueryArgument";
import getCommonServerSideProps from "@/lib/getCommonServerSideProps";
import { getHref } from "@/lib/helpers";
import QueryEvents from "@/queries/craft/QueryEvents.graphql";
import QueryEventsIndex from "@/queries/craft/QueryEventsIndex.graphql";
import state from "@/state";
import Head from "next/head";
import React, { useEffect } from "react";

export async function getServerSideProps({
  preview,
  params,
  req,
  res,
  query,
  locale,
}) {
  const commonProps = await getCommonServerSideProps({
    req,
    res,
    query,
    locale,
  });

  // Fetch everything but the events
  const queries = [
    { document: QueryEventsIndex, variables: { site: commonProps.site } },
  ];

  const response = await craftBatch(queries);

  const { pageEntry } = response[0];

  if (!pageEntry) {
    return {
      notFound: true,
    };
  }

  // Fetch the rest of the data using the organisation id if there is one
  const organisationsVariable = [":empty:"];

  if (commonProps.organisation?.[0]?.id) {
    organisationsVariable.push(commonProps.organisation[0].id);
  }

  const eventsQuery = [
    {
      document: QueryEvents,
      variables: {
        afterNow: getAfterNowQueryArgument(),
        organisations: organisationsVariable,
      },
    },
  ];

  const eventsResponse = await craftBatch(eventsQuery);

  const { events } = eventsResponse[0];

  return {
    props: {
      ...commonProps,
      pageEntry,
      events,
    },
  };
}

const Events = ({ site, pageEntry, events }) => {
  //	Track event
  useTrack(
    "View Event Index",
    "EventIndex",
    pageEntry.id,
    pageEntry.title,
    pageEntry.uri,
  );

  // If there's no hero, set text to dark
  useEffect(() => {
    state.header.theme = "dark";
  }, []);

  return (
    <Page withHero={false}>
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      {site !== "default" && (
        <Head>
          <meta key="robots" name="robots" content="none" />
        </Head>
      )}
      <Block spaceAbove="single" spaceBelow="double">
        <Heading
          heading={pageEntry.heading}
          standfirst={pageEntry.standfirst}
          headingType={"h1"}
        />
      </Block>

      <HorizontalRule />

      <Block spaceAbove="single" spaceBelow="single">
        <Heading
          heading="Coming up, and yours to join"
          standfirst="Expert led sessions for in-demand skills"
          headingType={"h2"}
          layout="vertical"
          alignment="center"
        />
      </Block>

      <EventsCarousel events={pageEntry.featuredEvents} />

      <HorizontalRule />

      <Block spaceAbove="double" spaceBelow="none" backgroundColor="#f3f4f6">
        <Heading
          heading="Your live programme in full"
          headingType={"h2"}
          layout="vertical"
          alignment="center"
        />
      </Block>
      {!!events && <EventIndex events={events} />}
    </Page>
  );
};

const EventsLayout = (page) => {
  const { navigation, site, siteLogo, organisation, hideYcnBranding } =
    page.props;

  const [heroImage] = page.props.pageEntry.heroImage;

  const breadcrumbs = [
    {
      key: "events",
      label: page.props.pageEntry.title,
      href: getHref(page.props.pageEntry),
    },
  ];

  return (
    <Layout
      site={site}
      navigation={navigation}
      withHero={!!heroImage}
      breadcrumbs={breadcrumbs}
      siteLogo={siteLogo}
      organisation={organisation}
      hideYcnBranding={hideYcnBranding}
    >
      {page}
    </Layout>
  );
};

Events.getLayout = EventsLayout;

Events.displayName = "Events";

export default Events;
